import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PasswordInput from '@/components/PasswordInput';
import ResetPasswordPopup from '@/components/ResetPasswordPopup';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { getPostAuthRedirect, getRequestedRedirect, withNextParam } from '@/utils/authRedirect';
import styles from './styles.module.scss';
import { VERBATIM_INPUT_PROPS } from '@/constants';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="#0496FF"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" /></svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16"><path fill="#EA4335" d="M12 11v3.2h4.5c-.2 1.2-1.4 3.4-4.5 3.4-2.7 0-4.9-2.2-4.9-5s2.2-5 4.9-5c1.5 0 2.6.6 3.2 1.2l2.2-2.1C15.9 5.3 14.1 4.5 12 4.5 7.9 4.5 4.5 7.9 4.5 12s3.4 7.5 7.5 7.5c4.3 0 7.2-3 7.2-7.3 0-.5 0-.9-.1-1.2H12z" /></svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M16.4 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.9-.9-3.1-.9-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.5 3 2.4 1.2 0 1.6-.8 3.1-.8 1.4 0 1.8.8 3.1.8 1.3 0 2.1-1.2 2.9-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.7-1-2.7-3.9zM14.2 5.7c.6-.8 1-1.9.9-3-1 0-2.1.7-2.8 1.5-.6.7-1.1 1.8-1 2.9 1.1.1 2.3-.6 2.9-1.4z" /></svg>
);

const ArrowIcon = () => (
  <svg className={styles.arrow} width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
    <path d="M1 6h12m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const LoginForm: React.FC = () => {
  const { t } = useTranslation('auth');
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useBreakpointDown('tablet');

  const pendingAction = (location.state as any)?.pendingAction;
  const requestedRedirect = getRequestedRedirect(location);
  const redirectTo = getPostAuthRedirect(location, '/dashboard');

  // Show info toast for pending action (invite flow)
  // toastId prevents duplicate toasts in React StrictMode (double useEffect in dev)
  React.useEffect(() => {
    if (pendingAction) {
      toast.info(t('pendingInviteAction'), {
        toastId: 'pending-invite-action',
      });
    }
  }, [pendingAction, t]);

  const {
    loginWithEmail,
    loginWithTelegram,
    loginWithGoogle,
    loginWithApple,
    isLoading,
    isTelegramAvailable,
  } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await loginWithEmail(data.email.trim().toLowerCase(), data.password);
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('loginError'));
    }
  };

  const onValidationError = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (errors.email) toast.error(t('emailInvalid'));
    else if (errors.password) toast.error(t('passwordRequired'));
  };

  const handleSocialLogin = async (method: () => Promise<any>, errorMsg: string) => {
    try {
      await method();
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || errorMsg);
    }
  };

  const handleResetPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMobile) {
      navigate('/forgot-password');
    } else {
      setResetPasswordOpen(true);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <article className={styles.loginCard}>
        <div className={styles.cardHead}>
          <span className={styles.eyebrow}>{t('seasonLabel')}</span>
          <h2 className={styles.cardTitle}>{t('signInAction')}</h2>
          <p className={styles.cardSub}>{t('useEmailOrSocial')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit, onValidationError)}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="login-email">{t('email')}</label>
            <input
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              id="login-email"
              type="text"
              placeholder="you@example.com"
              disabled={isLoading}
              data-testid="login-email"
              autoComplete="username"
              {...VERBATIM_INPUT_PROPS}
              {...register('email', {
                required: true,
                pattern: /^\S+@\S+$/i,
              })}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="login-password">{t('password')}</label>
            <Controller
              control={control}
              name="password"
              rules={{ required: true }}
              render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                <PasswordInput
                  error={!!error}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder="••••••••"
                  innerRef={ref}
                  disabled={isLoading}
                  id="login-password"
                  data-testid="login-password"
                />
              )}
            />
          </div>

          <div className={styles.row}>
            <label className={styles.check}>
              <Controller
                control={control}
                name="rememberMe"
                render={({ field: { onChange, value } }) => (
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
              {t('rememberMe')}
            </label>
            <button type="button" className={styles.textBtn} onClick={handleResetPassword}>
              {t('forgotPassword')}
            </button>
          </div>

          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            type="submit"
            disabled={isLoading}
            data-testid="login-submit"
          >
            {isLoading ? t('signingIn') : t('signInAction')}
            {!isLoading && <ArrowIcon />}
          </button>
        </form>

        <div className={styles.divider}>
          <span />
          <em>{t('orContinueWith')}</em>
          <span />
        </div>

        <div className={styles.socials}>
          {isTelegramAvailable && (
            <button
              type="button"
              className={styles.social}
              onClick={() => handleSocialLogin(loginWithTelegram, t('telegramSignInError'))}
              disabled={isLoading}
            >
              <TelegramIcon />
              {t('telegram')}
            </button>
          )}
          <button
            type="button"
            className={styles.social}
            onClick={() => handleSocialLogin(loginWithGoogle, t('googleSignInError'))}
            disabled={isLoading}
          >
            <GoogleIcon />
            {t('google')}
          </button>
          <button
            type="button"
            className={styles.social}
            onClick={() => handleSocialLogin(loginWithApple, t('appleSignInError'))}
            disabled={isLoading}
          >
            <AppleIcon />
            {t('apple')}
          </button>
        </div>

        <div className={styles.cardFoot}>
          <span>{t('dontHaveAccount')}</span>
          <Link
            to={withNextParam('/sign-up', requestedRedirect)}
            className={styles.textBtn}
            data-testid="login-signup-link"
          >
            {t('createOne')}
          </Link>
        </div>

      </article>

      <aside className={styles.peekCard} aria-hidden="true">
        <div className={styles.peekHead}>
          <span className={styles.peekLeague}>Champions League</span>
          <span className={styles.peekChip}>Live 67&apos;</span>
        </div>
        <div className={styles.peekTeams}>
          <div className={styles.peekTeam}>Real</div>
          <div className={styles.peekScore}>
            <div className={styles.peekBox}>3</div>
            <div className={styles.peekDash}>&ndash;</div>
            <div className={styles.peekBox}>2</div>
          </div>
          <div className={styles.peekTeam}>Bayern</div>
        </div>
      </aside>

      <ResetPasswordPopup
        open={resetPasswordOpen}
        onClose={() => setResetPasswordOpen(false)}
      />
    </div>
  );
};

export default LoginForm;
